import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronDown,
  faChevronUp,
  faEllipsisVertical,
  faEnvelope,
  faPaperPlane,
  faPenToSquare,
  faTrash,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import { defaultLoanStatus, loanStatusData } from "./chase-list-data";
import React, { Fragment, useRef, useState } from "react";
import { useChaseMenu } from "../../store/use-chase-menu";
import { Menu, Portal, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import { ParseErrors } from "../../utils/ParseErrors";
import axiosInstance from "../../utils/axiosInstance";
import EditChaseModel from "../../models/chase-edit-model";
import { useChaseEditModel } from "../../store/use-chase-edit-model";
import moment from "moment";
import NotesModal from "../../models/notes-model";
import { IChase } from "../../types/chase-type";
import ChaseDropMenu from "./chase-drop-menu";
import { useConfirmationModel } from "../../providers/confirmation-model";

interface TChaseRenderitem {
  item: IChase;
  index: number;
}

const getStatus = (status: string) =>
  loanStatusData.find((stat) => stat.name === status) ?? defaultLoanStatus;

/** How the chase was raised, shown as a tag beside the customer name. */
const CREATED_FROM: Record<string, { label: string; title: string }> = {
  A: { label: "Auto", title: "Created automatically" },
  M: { label: "Manual", title: "Created manually" },
};

/**
 * One row of the ⋮ menu. `tone` separates routine actions from destructive;
 * `active` is Headless UI's highlight, so pointer and keyboard read alike.
 */
const MenuItem: React.FC<{
  icon: IconDefinition;
  label: string;
  onSelect: () => void;
  tone?: "default" | "destructive";
  active?: boolean;
}> = ({ icon, label, onSelect, tone = "default", active = false }) => (
  <button
    type="button"
    onClick={onSelect}
    className={clsx(
      "flex w-full items-center gap-2.5 px-4 py-2 text-left text-[12px] font-medium transition-colors duration-200 focus:outline-none",
      tone === "destructive"
        ? clsx("text-rose-500 hover:bg-rose-500/10", active && "bg-rose-500/10")
        : clsx(
            "text-text-secondary hover:bg-overlay-mid hover:text-text-primary",
            active && "bg-overlay-mid text-text-primary"
          )
    )}
  >
    <FontAwesomeIcon icon={icon} className="w-[13px] text-[12px]" />
    {label}
  </button>
);

const MenuDivider = () => <div className="my-1 h-px bg-edge-faint" />;

const ChaseRenderitem: React.FC<TChaseRenderitem> = ({
  item: itemData,
  index,
}) => {
  const [item, setItem] = useState<IChase | null>(itemData);
  const [isMessagePopup, setIsMessagePopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // The menu is portalled out of the table's overflow container, so it is
  // positioned from the button's rect rather than by `absolute` inside the cell.
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });

  const PositionMenu = () => {
    const rect = menuButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  };
  const { initialValues, onToggleChaseMenu, onChaseMenuOpen } = useChaseMenu();
  const { showModal, hideModal } = useConfirmationModel();
  const { initialValues: editInitialValues, onOpen: onEditOpen } =
    useChaseEditModel();

  // --------------------------- [ DELETE CHASE ] ------------------------------
  const HandleDeleteChase = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.delete(`/chase/${item?.id}`);
      setLoading(false);
      setItem(null);
      toast.success(res.data?.message);
      hideModal();
    } catch (error) {
      toast.error(ParseErrors(error));
      hideModal();
      setLoading(false);
    }
  };
  // --------------------------- [ HANDLE ON EDIT CALL ] ------------------------------

  const HandleEditChase = () => {
    onEditOpen({ id: item?.id, data: item });
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------

  const UpdateItemData = async () => {
    try {
      const res = await axiosInstance.get(`/chase?id=${item?.id}`);
      const resData = res.data?.data[0];
      onChaseMenuOpen({
        id: resData?.id,
        chase: resData,
      });
      setItem(resData);
    } catch (error) {
      console.log(error);
    }
  };

  // ------------------------------- [ UPDATE ITEM DATA ] ------------------------------

  const ToggleMessagesPopup = () => {
    setIsMessagePopup(!isMessagePopup);
  };

  // ------------------------------------------ [ UPDATE WORKFLOW STATUS ] ------------------------------

  const UpdateWorkflowStatus = async (status: string) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/chase/update-trevor-status/${item?.policyReferenceNumber}`,
        {
          status: status,
        }
      );
      UpdateItemData();
      toast.success(res.data?.message);
      setLoading(false);
      hideModal();
    } catch (error) {
      console.log(error);
      hideModal();
      toast.error(ParseErrors(error));
      setLoading(false);
    }
  };

  if (!item) {
    return null;
  }

  const status = getStatus(item?.status);
  const createdFrom = CREATED_FROM[item?.createdFrom];
  const isExpanded = initialValues?.id === item?.id;
  const canChangeWorkflow = [
    "Auto-Renewals",
    "Pending-Invite",
    "Inviting",
    "Payment-Failed",
  ].includes(item?.status);

  return (
    <>
      <tr
        className="relative font-medium border-b border-edge-faint transition-colors duration-200 hover:bg-overlay-hover"
        key={index}
      >
        <td className="w-[20%] py-3.5 pl-1 align-top">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <p className="text-[14px] font-semibold text-text-primary">
              {item?.forename + " " + item.surname}
            </p>
            {createdFrom ? (
              <span
                title={createdFrom.title}
                className="shrink-0 rounded-full border border-edge px-2 py-[3px] text-[9px] font-bold uppercase tracking-[0.08em] text-light-gray"
              >
                {createdFrom.label}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-[12px] text-light-gray">
            {item?.policyReferenceNumber}
          </p>
        </td>

        <td className="w-[21%] py-3.5 align-top">
          <p className="text-[13px] text-text-primary break-all">
            {item?.email}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray tabular-nums">
            {item?.telephone}
          </p>
        </td>

        <td className="w-[14%] py-3.5 align-top">
          <p className="text-[13px] text-text-primary whitespace-nowrap">
            {moment(item?.startDate).format("DD MMM YYYY")}
          </p>
          <p className="mt-0.5 text-[12px] text-light-gray whitespace-nowrap">
            Renews {moment(item?.renewalDate).format("DD MMM YYYY")}
          </p>
        </td>

        <td className="w-[10%] py-3.5 align-middle text-center">
          {item?.quote?.premium ? (
            <p className="text-[14px] font-bold text-text-primary tabular-nums whitespace-nowrap">
              £
              {item?.quote?.premium?.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          ) : (
            <p className="text-[13px] text-light-gray whitespace-nowrap">
              Awaiting
            </p>
          )}
        </td>

        <td className="w-[11%] py-3.5 align-middle text-center">
          <p className="text-[13px] text-text-primary whitespace-nowrap capitalize">
            {item?.paymentType}
          </p>
        </td>

        <td className="w-[12%] py-3.5 align-middle">
          <div
            className={clsx(
              "inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-full",
              status.bgColor
            )}
          >
            <FontAwesomeIcon
              icon={status.icon}
              className={clsx("text-[11px]", status.color)}
            />
            <p
              className={clsx(
                "text-[11px] font-semibold whitespace-nowrap",
                status.color
              )}
            >
              {status.name?.replaceAll("-", " ")}
            </p>
          </div>
        </td>

        <td className="py-3.5 align-middle">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={ToggleMessagesPopup}
              title="Policy notes"
              aria-label="Policy notes"
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-light-gray transition-colors duration-200 hover:bg-overlay hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-[15px]" />
            </button>
          </div>
        </td>

        <td className="px-2 py-3.5 align-middle">
          <Menu as="div" className="inline-block text-left">
            <Menu.Button
              ref={menuButtonRef}
              onClick={PositionMenu}
              title="Chase actions"
              aria-label="Chase actions"
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-light-gray transition-colors duration-200 hover:bg-overlay hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            >
              <FontAwesomeIcon
                icon={faEllipsisVertical}
                className="text-[15px]"
              />
            </Menu.Button>

            <Portal>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  style={{ top: menuPos.top, right: menuPos.right }}
                  className="fixed z-[99999] min-w-[184px] overflow-hidden rounded-xl border border-edge bg-surface-elevated py-1 shadow-overlay focus:outline-none"
                >
                  <Menu.Item>
                    {({ active }) => (
                      <MenuItem
                        active={active}
                        icon={faPenToSquare}
                        label="Edit chase"
                        onSelect={HandleEditChase}
                      />
                    )}
                  </Menu.Item>

                  {canChangeWorkflow ? (
                    <>
                      <MenuDivider />
                      <Menu.Item>
                        {({ active }) => (
                          <MenuItem
                            active={active}
                            icon={faPaperPlane}
                            label="Set to manual"
                            onSelect={() =>
                              showModal({
                                onClick: () => {
                                  UpdateWorkflowStatus("invite");
                                },
                                type: "manual",
                                loading: loading,
                              })
                            }
                          />
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <MenuItem
                            active={active}
                            icon={faXmarkCircle}
                            label="Set to lapse"
                            onSelect={() =>
                              showModal({
                                onClick: () => {
                                  UpdateWorkflowStatus("lapse");
                                },
                                type: "lapse",
                                loading: loading,
                              })
                            }
                          />
                        )}
                      </Menu.Item>
                    </>
                  ) : null}

                  <MenuDivider />
                  <Menu.Item>
                    {({ active }) => (
                      <MenuItem
                        active={active}
                        icon={faTrash}
                        label="Delete chase"
                        tone="destructive"
                        onSelect={() =>
                          showModal({
                            type: "delete",
                            onClick: HandleDeleteChase,
                            loading: loading,
                          })
                        }
                      />
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Portal>
          </Menu>
        </td>

        <td className="py-3.5 align-middle">
          <button
            type="button"
            onClick={() =>
              onToggleChaseMenu({
                id: item?.id,
                chase: item,
              })
            }
            title={isExpanded ? "Hide chase details" : "Show chase details"}
            aria-label={isExpanded ? "Hide chase details" : "Show chase details"}
            aria-expanded={isExpanded}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg text-light-gray transition-all duration-200 hover:bg-overlay hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              className="text-[13px]"
            />
          </button>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <ChaseDropMenu UpdateItemData={UpdateItemData} />
        </tr>
      )}

      {editInitialValues.id === item?.id && (
        <EditChaseModel UpdateItemData={UpdateItemData} />
      )}

      {isMessagePopup ? (
        <NotesModal
          onToggle={ToggleMessagesPopup}
          data={{ policyNumber: item?.policyReferenceNumber }}
        />
      ) : null}
    </>
  );
};

export default ChaseRenderitem;
